import { useMutation, useQuery } from '@apollo/client';
import AddIcon from '@clearkit/icons/glyphs/AddHollow';
import CheckIcon from '@clearkit/icons/glyphs/CheckmarkOrb';
import GoExternal from '@clearkit/icons/glyphs/GoExternal';
import InfoIcon from '@clearkit/icons/glyphs/Info';
import ConnectIcon from '@clearkit/icons/glyphs/Sync';
import { CKBadge, CKBox, CKButton, CKTextField } from '@clearkit/react';
import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';

import BooleanToggleSection from '~/components/configuration/BooleanToggleSection';
import FormSection from '~/components/configuration/FormSection';
import LoadingArea from '~/components/LoadingArea';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import { configurationNameForType } from '~/lib/configuration';

import addAllowedSiteMutation from './addAllowedSite.graphql';
import AllowedSiteMenu from './AllowedSiteMenu';
import query from './query.graphql';
import removeAllowedSiteMutation from './removeAllowedSite.graphql';
import updateConfigurationMutation from './updateConfiguration.graphql';

const IdentifyHelpLinks = {
  hubspot:
    'https://help.clearbit.com/hc/en-us/articles/4420179541911-Set-Up-Clearbit-Forms-for-HubSpot-',
  marketo: 'https://help.clearbit.com/hc/en-us/articles/4420189078807',
  pardot:
    'https://help.clearbit.com/hc/en-us/articles/4420193089047-Set-Up-Clearbit-Forms-for-Pardot-',
};

export default function FormFieldMappings({ integration }) {
  const {
    data: { formConfiguration = { initialLoading: true } } = {},
    loading,
    refetch,
  } = useQuery(query, {
    variables: {
      integration,
    },
    notifyOnNetworkStatusChange: true,
  });
  const [removeAllowedSite] = useMutation(removeAllowedSiteMutation);
  const [addAllowedSite, { loading: addSiteLoading }] = useMutation(
    addAllowedSiteMutation,
  );
  const [updateConfiguration] = useMutation(updateConfigurationMutation);

  const { allowedSites = [] } = formConfiguration;

  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [addSiteErrors, setAddSiteErrors] = useState([]);
  const [newSiteUrl, setNewSiteUrl] = useState('');

  const { enabled: identifyEnabled } = useFeatureFlag(
    'x_forms_identify_on_submit',
  );

  function updateFlags(update) {
    return updateConfiguration({
      variables: {
        input: {
          integration,
          autofill: formConfiguration.autofill,
          formShortening: formConfiguration.formShortening,
          identifyOnSubmit: formConfiguration.identifyOnSubmit,
          ...update,
        },
      },
    });
  }

  return (
    <LoadingArea loading={loading && formConfiguration.initialLoading}>
      <div className="divide-y divide-gray-200">
        <FormSection
          className="mb-9"
          title="Forms setup and allowed sites"
          description={
            <>
              <p className="mb-8 text-gray-600">
                Specify the sites where Clearbit form shortening and autofill
                should work. Enter a specific URL (e.g. example.com or
                example.com/blog) to autofill or shorten forms at that address,
                or enter a wildcard (e.g. example.com/* or *.example.com/*) to
                allow autofill and shortening for all forms on that domain.
              </p>
              <CKButton
                as={'a'}
                target="_blank"
                rel="noopener noreferrer"
                href="https://help.clearbit.com/hc/en-us/articles/4420176895127"
                rightIcon={<GoExternal />}
                variantColor="blue">
                Forms installation guide
              </CKButton>
            </>
          }>
          <CKBox>
            <div className="px-6 py-3 text-base font-medium text-gray-600 border-b border-gray-200">
              Allowed sites
            </div>
            <div className="divide-y divide-gray-100">
              {allowedSites.map((siteInfo) => (
                <div
                  key={siteInfo.url}
                  className="flex items-center justify-between w-full px-6 py-2 gap-4">
                  <div className="flex-grow">
                    <CKBadge>{siteInfo.siteUrl}</CKBadge>
                  </div>
                  <div
                    className={classnames('flex items-center', {
                      'text-green': siteInfo.eventsReceived,
                      'text-yellow': !siteInfo.eventsReceived,
                    })}>
                    {siteInfo.eventsReceived ? (
                      <>
                        <CheckIcon className="relative mr-1 bottom-px right-px fill-gradient-br-green" />
                        <span className="text-sm font-medium">
                          Receiving events
                        </span>
                      </>
                    ) : (
                      <>
                        <InfoIcon className="relative mr-1 bottom-px right-px fill-gradient-br-yellow" />
                        <span className="text-sm font-medium">
                          Not receiving events
                        </span>
                      </>
                    )}
                  </div>
                  <AllowedSiteMenu
                    onDelete={() => {
                      removeAllowedSite({
                        variables: {
                          input: {
                            integration,
                            id: siteInfo.id,
                          },
                        },
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 rounded-b bg-gray-50">
              {isAddingDomain ? (
                <>
                  <div className="flex gap-2">
                    <CKTextField
                      className="flex-grow"
                      value={newSiteUrl}
                      variant={addSiteErrors.length > 0 ? 'error' : undefined}
                      onChange={(e) => {
                        setNewSiteUrl(e.target.value);
                      }}></CKTextField>

                    <CKButton
                      variant={CKButton.Variant.bold}
                      variantColor={CKButton.VariantColor.blue}
                      isLoading={addSiteLoading}
                      disabled={isEmpty(newSiteUrl)}
                      onClick={async () => {
                        let { data } = await addAllowedSite({
                          variables: {
                            input: {
                              integration,
                              siteUrl: newSiteUrl,
                            },
                          },
                        });

                        const { errors } = data.response;
                        const siteErrors = errors.find(
                          (e) => e.field === 'allowed_site',
                        );

                        if (siteErrors) {
                          setAddSiteErrors(siteErrors.messages);
                        } else {
                          setIsAddingDomain(false);
                          setNewSiteUrl('');
                        }
                      }}>
                      Save
                    </CKButton>
                    <CKButton
                      onClick={() => {
                        setIsAddingDomain(false);
                        setNewSiteUrl('');
                      }}>
                      Cancel
                    </CKButton>
                  </div>
                  {addSiteErrors.length > 0 && (
                    <div className="my-2 text-red-600">
                      <ul className="ml-6 list-disc">
                        {addSiteErrors.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-between gap-2">
                  <CKButton
                    leftIcon={<AddIcon />}
                    variantColor="blue"
                    onClick={() => {
                      setIsAddingDomain(true);
                    }}>
                    Add domain
                  </CKButton>

                  {allowedSites.length ? (
                    <CKButton
                      isLoading={loading}
                      leftIcon={<ConnectIcon transform="rotate(90)" />}
                      disabled={!allowedSites.length}
                      className="flex items-center"
                      variantColor="blue"
                      onClick={() => refetch()}>
                      Test implementation
                    </CKButton>
                  ) : null}
                </div>
              )}
            </div>
          </CKBox>
        </FormSection>
        <FormSection
          className="pt-9"
          title="Form settings"
          description={
            <p className="text-gray-600">
              Choose whether you'd like to have Clearbit autofill form fields,
              and whether or not you'd like to keep form fields hidden if
              Clearbit has data for them.
            </p>
          }>
          <CKBox>
            <div className="divide-y divide-gray-100">
              <BooleanToggleSection
                id="form_autofill"
                title="Form field autofill"
                description={
                  <p>
                    When enabled, Clearbit will enrich an email address and
                    autofill {configurationNameForType(integration)} forms with
                    data in{' '}
                    <Link to={`/forms/${integration}/mappings`}>
                      mapped form fields
                    </Link>
                    .
                  </p>
                }
                checked={formConfiguration.autofill}
                onChange={async (value) => {
                  await updateFlags({ autofill: value });
                }}
              />
              <BooleanToggleSection
                id="form_shortening"
                title="Form shortening"
                description="When enabled, form fields that clearbit can autofill remain hidden, keeping forms to just fields without data."
                checked={formConfiguration.formShortening}
                disabled={!formConfiguration.autofill}
                onChange={async (value) => {
                  await updateFlags({ formShortening: value });
                }}
              />
              {identifyEnabled && (
                <BooleanToggleSection
                  id="form_identify"
                  title="Leverage Forms to Track Web Activity"
                  description={
                    <p>
                      Automatically track web activity for users who submit a
                      Clearbit-enabled form.{' '}
                      <a href={IdentifyHelpLinks[integration]}>Learn more</a>.
                    </p>
                  }
                  checked={formConfiguration.identifyOnSubmit}
                  disabled={!formConfiguration.autofill}
                  onChange={async (value) => {
                    await updateFlags({ identifyOnSubmit: value });
                  }}
                />
              )}
            </div>
            {identifyEnabled ? (
              <div className="flex items-center px-6 py-3 font-medium border-t border-gray-200 rounded-b bg-gray-50">
                <InfoIcon className="relative mr-1 text-gray bottom-px right-px" />
                <span className="text-sm italic text-gray">
                  Form shortening and tracking web activity depend on autofill
                  and require it to be enabled
                </span>
              </div>
            ) : (
              <div className="flex items-center px-6 py-3 font-medium border-t border-gray-200 rounded-b bg-gray-50">
                <InfoIcon className="relative mr-1 text-gray bottom-px right-px" />
                <span className="text-sm italic text-gray">
                  Form shortening depends on autofill and requires it to be
                  enabled
                </span>
              </div>
            )}
          </CKBox>
        </FormSection>
      </div>
    </LoadingArea>
  );
}
